import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Settings from '../Settings';
import { useLanguage } from '../../context/LanguageContext';
import { signInWithGoogle, logout, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getProfileNickname } from '../../utils/profile';
import { getLanguageFromPath, localizedPath, localizeCurrentPath, setActiveLanguage } from '../../utils/routes';

import {
  LayoutDashboard, ClipboardList, Package, Home,
  BookOpen, LogOut, LogIn, ChevronDown, Minus, Plus,
  Settings as SettingsIcon, Map, MessageSquarePlus, User,
  X, Send
} from 'lucide-react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

const DiscordIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 127 96" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.09,105.09,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.11,77.11,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,6.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.89,105.89,0,0,0,126.6,80.22c2.36-24.44-2.54-49.38-18.9-72.15ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z" />
  </svg>
);

function Navbar({ profile, onReset, onUpdate }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [showSettings, setShowSettings] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [hideoutData] = useState([]);
  const [user, setUser] = useState(null);

  // SPAM KORUMASI İÇİN YENİ STATE EKLENDİ
  const [isSendingFeedback, setIsSendingFeedback] = useState(false);

  const { t, changeLanguage, language } = useLanguage();
  const profileMenuRef = useRef(null);

  const [localLevel, setLocalLevel] = useState(profile.level);
  const [localFenceRep, setLocalFenceRep] = useState(profile.fenceRep);

  const DISCORD_INVITE_LINK = "https://discord.gg/6fnEzwqybZ";
  const DISCORD_WEBHOOK_URL = process.env.REACT_APP_DISCORD_FEEDBACK_WEBHOOK;
  const operatorName = getProfileNickname(profile, 'Guest');
  const routeLanguage = getLanguageFromPath(location.pathname);
  const navigationLanguage = routeLanguage || language || profile?.settings?.language || 'en';

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const targetLanguage = routeLanguage || profile?.settings?.language;
    if (targetLanguage && targetLanguage !== language) {
      changeLanguage(targetLanguage);
    }
  }, [profile?.settings?.language, routeLanguage, language, changeLanguage]);

  useEffect(() => {
    setLocalLevel(profile.level);
    setLocalFenceRep(profile.fenceRep);
  }, [profile.level, profile.fenceRep]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) setShowProfileDropdown(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const openDiscordServer = () => { window.open(DISCORD_INVITE_LINK, '_blank'); };

  // SPAM KORUMALI YENİ FONKSİYON
  const sendFeedbackToDiscord = async () => {
    if (!feedbackMessage.trim() || isSendingFeedback) return;
    if (!DISCORD_WEBHOOK_URL) {
      alert("Feedback is not configured yet.");
      return;
    }

    setIsSendingFeedback(true); // Butonu kilitle

    const payload = { content: `**Feedback from ${operatorName}**\nLevel: ${profile.level}\nMessage: ${feedbackMessage}` };
    try {
      await fetch(DISCORD_WEBHOOK_URL, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      alert("Feedback sent! Thank you.");
      setFeedbackMessage("");
      setShowFeedback(false);
    } catch (error) {
      alert("Failed to send feedback.");
    } finally {
      setIsSendingFeedback(false); // İşlem bitti, butonu geri aç
    }
  };

  const handleAuth = async () => {
    if (user) { await logout(); } else { await signInWithGoogle(); }
  };

  const handleLanguageChange = (langCode) => {
    const nextPath = localizeCurrentPath(location.pathname, langCode);
    const updatedProfile = { ...profile, settings: { ...(profile?.settings || {}), language: langCode } };

    setActiveLanguage(langCode);
    navigate(nextPath, { replace: true });
    changeLanguage(langCode);
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
    onUpdate(updatedProfile);
  };

  const handleNavigation = (path) => {
    setShowProfileDropdown(false);
    navigate(localizedPath(path, navigationLanguage));
  };
  const isRouteActive = (routeId) => {
    const routePath = localizedPath(routeId, navigationLanguage);
    return location.pathname === routePath || (location.pathname === '/' && routeId === 'dashboard') || location.pathname.startsWith(`${routePath}/`);
  };

  const handleLevelChange = (delta) => { const newLvl = Math.max(1, Math.min(79, profile.level + delta)); updateProfileLevel(newLvl); };
  const updateProfileLevel = (newLvl) => { const up = { ...profile, level: newLvl }; localStorage.setItem('tarkovProfile', JSON.stringify(up)); onUpdate(up); };
  const commitLevelChange = () => { let val = parseInt(localLevel); if (isNaN(val) || val < 1) val = 1; if (val > 79) val = 79; updateProfileLevel(val); setLocalLevel(val); };

  const handleFenceRepChange = (delta) => { const newRep = Math.max(-20, Math.min(20, profile.fenceRep + delta)); updateProfileFenceRep(parseFloat(newRep.toFixed(2))); };
  const updateProfileFenceRep = (newRep) => { const up = { ...profile, fenceRep: newRep }; localStorage.setItem('tarkovProfile', JSON.stringify(up)); onUpdate(up); };
  const commitFenceRepChange = () => { let val = parseFloat(localFenceRep); if (isNaN(val)) val = 0.00; if (val < -20) val = -20.00; if (val > 20) val = 20.00; val = parseFloat(val.toFixed(2)); updateProfileFenceRep(val); setLocalFenceRep(val); };

  const handleEditionChange = (newEdition) => {
    let updatedHideout = { ...profile.hideoutModules };
    let targetStashLevel = 1;
    if (newEdition === 'unheard' || newEdition === 'edgeOfDarkness') targetStashLevel = 4;
    else if (newEdition === 'prepareForEscape') targetStashLevel = 3;
    else if (newEdition === 'leftBehind') targetStashLevel = 2;

    // Optional chaining added to prevent errors if hideoutData is empty
    const stashModule = hideoutData?.find(m => m.name === 'Stash');
    const stashId = stashModule ? stashModule.id : '5d484fc0654e76006657e0ab';
    updatedHideout[stashId] = targetStashLevel;

    const cultistModule = hideoutData?.find(m => m.name === 'Cultist Circle');
    const cultistId = cultistModule ? cultistModule.id : '667298e75ea6b4493c08f266';

    if (newEdition === 'unheard') { updatedHideout[cultistId] = 1; }
    else { delete updatedHideout[cultistId]; }

    const updatedProfile = { ...profile, gameEdition: newEdition, hideoutModules: updatedHideout };
    localStorage.setItem('tarkovProfile', JSON.stringify(updatedProfile));
    onUpdate(updatedProfile);
  };

  const handleFactionChange = (newFaction) => { const up = { ...profile, faction: newFaction }; localStorage.setItem('tarkovProfile', JSON.stringify(up)); onUpdate(up); };
  const handlePrestigeChange = (newPrestige) => { const up = { ...profile, prestige: newPrestige }; localStorage.setItem('tarkovProfile', JSON.stringify(up)); onUpdate(up); };

  const navItems = [
    { id: 'dashboard', name: t('dashboard'), icon: LayoutDashboard },
    { id: 'quests', name: t('quests'), icon: ClipboardList },
    { id: 'kappa-items', name: t('kappa_items'), icon: Package },
    { id: 'hideout', name: t('hideout'), icon: Home },
  ];

  const editionLabels = { 'standard': 'Standard', 'leftBehind': 'Left Behind', 'prepareForEscape': 'Prepare For Escape', 'edgeOfDarkness': 'Edge of Darkness', 'unheard': 'Unheard' };
  const prestigeLogos = { 1: '/Images/P1.webp', 2: '/Images/P2.webp', 3: '/Images/P3.webp', 4: '/Images/P4.webp', 5: '/Images/P5.webp', 6: '/Images/P6.webp' };
  const currentPrestige = profile.prestige || 0;

  const getLevelRankImage = (level) => {
    const safeLevel = parseInt(level) || 1;
    if (safeLevel >= 1 && safeLevel <= 4) return '/Images/Rank1.webp';
    if (safeLevel >= 5 && safeLevel <= 9) return '/Images/Rank5.webp';
    if (safeLevel >= 10 && safeLevel <= 14) return '/Images/Rank10.webp';
    if (safeLevel >= 15 && safeLevel <= 19) return '/Images/Rank15.webp';
    if (safeLevel >= 20 && safeLevel <= 24) return '/Images/Rank20.webp';
    if (safeLevel >= 25 && safeLevel <= 29) return '/Images/Rank25.webp';
    if (safeLevel >= 30 && safeLevel <= 34) return '/Images/Rank30.webp';
    if (safeLevel >= 35 && safeLevel <= 39) return '/Images/Rank35.webp';
    if (safeLevel >= 40 && safeLevel <= 44) return '/Images/Rank40.webp';
    if (safeLevel >= 45 && safeLevel <= 49) return '/Images/Rank45.webp';
    if (safeLevel >= 50 && safeLevel <= 54) return '/Images/Rank50.webp';
    if (safeLevel >= 55 && safeLevel <= 59) return '/Images/Rank55.webp';
    if (safeLevel >= 60 && safeLevel <= 64) return '/Images/Rank60.webp';
    if (safeLevel >= 65 && safeLevel <= 69) return '/Images/Rank65.webp';
    if (safeLevel >= 70 && safeLevel <= 74) return '/Images/Rank70.webp';
    if (safeLevel >= 75) return '/Images/Rank75.webp';
    return '/Images/Rank1.webp';
  };

  const getPrestigeGradient = (prestige) => {
    switch (prestige) {
      case 1: return 'from-gray-800 to-zinc-900';
      case 2: return 'from-zinc-800 to-slate-900';
      case 3: return 'from-orange-900 to-red-950';
      case 4: return 'from-red-900 to-orange-950';
      case 5: return 'from-emerald-900 to-teal-950';
      case 6: return 'from-teal-900 to-emerald-950';
      default: return 'from-zinc-900 to-black';
    }
  };

  return (
    <>
      <style>{`
        /* Yeni sayfalar eklendikçe çirkin scrollbar çıkmasını engeller */
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <nav className="bg-[#050505]/95 backdrop-blur-md border-b border-zinc-800 sticky top-0 z-50 h-16 w-full">
        <div className="w-full px-4 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full gap-2 lg:gap-4">

            {/* SOL KISIM: Logo */}
            <div className="flex items-center gap-3 cursor-pointer group flex-shrink-0" onClick={() => handleNavigation('dashboard')}>
              <div className="bg-[#09090b] border border-zinc-700 w-10 h-10 rounded-sm p-1.5 flex flex-col gap-0.5 group-hover:border-emerald-500 transition-colors shadow-lg">
                <div className="flex gap-0.5 h-full"><div className="bg-zinc-700 w-full rounded-none"></div><div className="bg-emerald-500 w-full rounded-none shadow-[0_0_8px_rgba(16,185,129,0.6)]"></div></div>
                <div className="flex gap-0.5 h-full"><div className="bg-zinc-700 w-full rounded-none"></div><div className="bg-zinc-700 w-full rounded-none"></div></div>
              </div>
              <div className="hidden sm:flex flex-col">
                <span className="text-white font-black text-xl tracking-tighter leading-none group-hover:text-emerald-500 transition-colors">TARKOVSTASH</span>
                <span className="text-emerald-600 text-xs font-bold tracking-widest leading-none text-right">.COM</span>
              </div>
            </div>

            <div className="h-8 w-px bg-zinc-800 hidden lg:block flex-shrink-0 mx-2"></div>

            {/* ORTA KISIM: Menü Linkleri (Geleceğe Hazır Scrollable Alan) */}
            {/* min-w-0 ve overflow-x-auto eklendi. İleride 10 sayfa bile eklesen sağa taşırmaz, kendi içinde kayar. */}
            <div className="hidden md:flex items-center gap-1 flex-1 min-w-0 overflow-x-auto no-scrollbar py-2">
              {navItems.map(item => {
                const Icon = item.icon;
                const isActive = isRouteActive(item.id);
                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.id)}
                    className={`flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 rounded-sm text-xs font-bold tracking-wide transition-all duration-200 flex-shrink-0 whitespace-nowrap
                      ${isActive ? 'text-white bg-emerald-900/30 border border-emerald-500/50' : 'text-zinc-400 hover:text-white hover:bg-zinc-800 border border-transparent'}`}
                  >
                    <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-emerald-400' : ''}`} strokeWidth={2.5} />
                    {item.name}
                  </button>
                );
              })}

              <div className="h-5 w-px bg-zinc-800 flex-shrink-0 mx-1.5"></div>

              <button
                onClick={() => handleNavigation('story')}
                className={`flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 rounded-sm text-xs font-bold tracking-wide transition-all duration-200 flex-shrink-0 whitespace-nowrap
                  ${isRouteActive('story') ? 'text-white bg-orange-900/20 border border-orange-500/50' : 'text-zinc-400 hover:text-white hover:bg-zinc-800 border border-transparent'}`}
              >
                <BookOpen className={`w-3.5 h-3.5 ${isRouteActive('story') ? 'text-orange-400' : ''}`} strokeWidth={2.5} />
                {t('story_guide')}
              </button>

              <button
                onClick={() => handleNavigation('kappa-path')}
                className={`flex items-center gap-1.5 px-2.5 lg:px-3 py-1.5 rounded-sm text-xs font-bold tracking-wide transition-all duration-200 flex-shrink-0 whitespace-nowrap
                  ${isRouteActive('kappa-path') ? 'text-white bg-purple-900/20 border border-purple-500/50' : 'text-zinc-400 hover:text-white hover:bg-zinc-800 border border-transparent'}`}
              >
                <Map className={`w-3.5 h-3.5 ${isRouteActive('kappa-path') ? 'text-purple-400' : ''}`} strokeWidth={2.5} />
                {t('kappa_path')}
              </button>
            </div>

            {/* SAĞ KISIM: Ayarlar ve Profil (flex-shrink-0 ile taşması önlendi) */}
            <div className="flex items-center gap-2 lg:gap-3 ml-auto flex-shrink-0">
              <div className="hidden sm:flex items-center bg-[#09090b] border border-zinc-700 rounded-sm p-1">
                {['en', 'tr', 'ru'].map(langCode => (
                  <button key={langCode} onClick={() => handleLanguageChange(langCode)} className={`px-2 py-1 text-[10px] font-black transition-all rounded-sm ${language === langCode ? 'bg-zinc-700 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}>
                    {langCode.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="hidden lg:flex items-center gap-2">
                <button onClick={openDiscordServer} className="w-9 h-9 flex items-center justify-center rounded-sm bg-[#5865F2]/10 text-[#5865F2] hover:bg-[#5865F2] hover:text-white transition-all border border-[#5865F2]/20 hover:border-[#5865F2]">
                  <DiscordIcon className="w-5 h-5" />
                </button>
                <button onClick={() => setShowFeedback(true)} className="w-9 h-9 flex items-center justify-center rounded-sm bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all border border-zinc-700 hover:border-zinc-600">
                  <MessageSquarePlus className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>

              <div className="h-6 w-px bg-zinc-800 hidden sm:block mx-1"></div>

              <button onClick={() => setShowSettings(true)} className="w-9 h-9 flex items-center justify-center rounded-sm bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all border border-zinc-700 hover:border-zinc-600">
                <SettingsIcon className="w-5 h-5" strokeWidth={2} />
              </button>

              <div className="relative" ref={profileMenuRef}>
                {/* TRUNCATE FİXİ: Profil butonu 160px ile 200px arasına kilitlendi, uzun isimler ... ile kesilir */}
                <button onClick={() => setShowProfileDropdown(!showProfileDropdown)} className={`flex items-center gap-3 px-2 lg:px-3 py-1.5 rounded-sm border transition-all group w-[150px] lg:w-[190px] flex-shrink-0 min-w-0 ${user ? 'bg-[#09090b] border-zinc-700 hover:border-emerald-500/50' : 'bg-emerald-900/10 border-emerald-500/50 hover:bg-emerald-900/20'}`}>
                  {user ? (
                    <>
                      <div className="w-8 h-8 flex-shrink-0 rounded-sm bg-zinc-800 flex items-center justify-center border border-zinc-600 group-hover:border-emerald-500/50 overflow-hidden">
                        <LazyLoadImage src={user.photoURL} alt={operatorName} effect="blur" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex flex-col items-start leading-none flex-1 min-w-0">
                        <span className="text-white text-xs font-bold tracking-wide group-hover:text-emerald-400 transition-colors truncate w-full text-left">{operatorName}</span>
                        <span className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider group-hover:text-zinc-400 transition-colors truncate w-full text-left">{editionLabels[profile.gameEdition] || 'Standard'}</span>
                      </div>
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 text-zinc-500 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                    </>
                  ) : (
                    <>
                      <div className="w-8 h-8 flex-shrink-0 rounded-sm bg-emerald-700 flex items-center justify-center text-white border border-emerald-500">
                        <User className="w-4 h-4" />
                      </div>
                      <div className="flex flex-col items-start leading-none flex-1 min-w-0">
                        <span className="text-emerald-400 font-bold text-sm uppercase tracking-wide truncate w-full text-left">
                          {operatorName}
                        </span>
                        <span className="text-[9px] text-zinc-500 font-bold uppercase tracking-wider truncate w-full text-left">{editionLabels[profile.gameEdition] || 'Standard'}</span>
                      </div>
                      <ChevronDown className={`w-3 h-3 flex-shrink-0 text-zinc-500 transition-transform ${showProfileDropdown ? 'rotate-180' : ''}`} />
                    </>
                  )}
                </button>

                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-80 lg:w-96 bg-[#09090b] border border-zinc-700 rounded-sm shadow-2xl z-50 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <div className={`bg-gradient-to-r ${getPrestigeGradient(currentPrestige)} px-4 py-3 border-b border-zinc-700 relative`}>
                      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent)]"></div>
                      <div className="flex items-center justify-between relative z-10">
                        <div>
                          <div className="text-xs text-white/50 uppercase tracking-wider mb-1 font-semibold">{t('profile_overview')}</div>
                          <div className="text-white font-bold flex items-center gap-2">
                            {operatorName.toUpperCase()}
                            {currentPrestige > 0 && <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded-sm border border-yellow-500/30 font-black">PRESTIGE {currentPrestige}</span>}
                          </div>
                        </div>
                        {currentPrestige > 0 && prestigeLogos[currentPrestige] && (
                          <LazyLoadImage src={prestigeLogos[currentPrestige]} alt="Prestige" effect="blur" className="w-12 h-12 object-contain drop-shadow-lg" />
                        )}
                      </div>
                    </div>

                    <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto custom-scrollbar">
                      <div className="bg-[#0c0c0e] rounded-sm p-3 border border-zinc-800">
                        <div className="text-zinc-500 text-xs mb-2 font-semibold uppercase tracking-wider">{t('game_edition')}</div>
                        <select value={profile.gameEdition} onChange={(e) => handleEditionChange(e.target.value)} className="w-full bg-[#050505] border border-zinc-700 text-white rounded-sm px-3 py-2.5 text-sm font-semibold focus:outline-none focus:border-emerald-500 transition cursor-pointer hover:border-zinc-600">
                          {Object.entries(editionLabels).map(([key, label]) => (
                            <option key={key} value={key} className="bg-[#050505]">{label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="bg-[#0c0c0e] rounded-sm p-3 border border-zinc-800 relative overflow-hidden">
                        <div className="absolute inset-0 z-0 pointer-events-none opacity-20 bg-center bg-no-repeat bg-contain" style={{ backgroundImage: `url(${getLevelRankImage(profile.level)})` }}></div>
                        <div className="relative z-10">
                          <div className="text-zinc-500 text-xs mb-2 font-semibold uppercase tracking-wider">{t('level')}</div>
                          <div className="flex items-center gap-3">
                            <button onClick={() => handleLevelChange(-1)} className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm font-bold transition flex items-center justify-center border border-zinc-700 hover:border-zinc-500"><Minus className="w-4 h-4" strokeWidth={2.5} /></button>
                            <div className="flex-1">
                              <input type="text" inputMode="numeric" pattern="[0-9]*" value={localLevel} onFocus={(e) => e.target.select()} onChange={(e) => { const val = e.target.value.replace(/\D/g, ''); setLocalLevel(val); }} onBlur={commitLevelChange} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} className="w-full text-center text-4xl font-black text-emerald-500 bg-transparent border border-zinc-700/50 rounded-sm py-2 focus:outline-none focus:border-emerald-500/50 transition drop-shadow-sm" />
                            </div>
                            <button onClick={() => handleLevelChange(1)} className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm font-bold transition flex items-center justify-center border border-zinc-700 hover:border-zinc-500"><Plus className="w-4 h-4" strokeWidth={2.5} /></button>
                          </div>
                        </div>
                      </div>

                      <div className="bg-[#0c0c0e] rounded-sm p-3 border border-zinc-800">
                        <div className="text-zinc-500 text-xs mb-3 font-semibold uppercase tracking-wider">{t('prestige_level')}</div>
                        <div className="grid grid-cols-7 gap-2">
                          {[0, 1, 2, 3, 4, 5, 6].map(prestige => (
                            <button key={prestige} onClick={() => handlePrestigeChange(prestige)} className={`aspect-square rounded-sm border transition-all duration-200 flex items-center justify-center ${currentPrestige === prestige ? 'border-yellow-500 bg-yellow-500/10 shadow-[0_0_10px_rgba(234,179,8,0.3)]' : 'border-zinc-700 bg-zinc-900 hover:bg-zinc-800 hover:border-zinc-500'}`}>
                              {prestige === 0 ? <span className="text-sm text-zinc-500 font-bold">-</span> : prestigeLogos[prestige] ? <LazyLoadImage src={prestigeLogos[prestige]} alt={`P${prestige}`} effect="blur" className="w-full h-full object-contain p-1" /> : <span className="text-xs text-yellow-500 font-bold">{prestige}</span>}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="border-t border-zinc-800 pt-3 space-y-3">
                        <div className="bg-[#0c0c0e] rounded-sm p-3 border border-zinc-800">
                          <div className="text-zinc-500 text-xs mb-2 font-semibold uppercase tracking-wider">{t('faction')}</div>
                          <div className="flex gap-2">
                            <button onClick={() => handleFactionChange('USEC')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-bold transition-all duration-200 border ${profile.faction === 'USEC' ? 'bg-blue-900/20 text-blue-400 border-blue-500' : 'bg-zinc-900 text-zinc-500 border-zinc-700 hover:text-zinc-300'}`}>USEC</button>
                            <button onClick={() => handleFactionChange('BEAR')} className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-bold transition-all duration-200 border ${profile.faction === 'BEAR' ? 'bg-red-900/20 text-red-400 border-red-500' : 'bg-zinc-900 text-zinc-500 border-zinc-700 hover:text-zinc-300'}`}>BEAR</button>
                          </div>
                        </div>

                        <div className="bg-[#0c0c0e] rounded-sm p-3 border border-zinc-800">
                          <div className="text-zinc-500 text-xs mb-2 font-semibold uppercase tracking-wider">{t('fence_rep')}</div>
                          <div className="flex items-center gap-3">
                            <button onClick={() => handleFenceRepChange(-0.01)} className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm font-bold transition flex items-center justify-center border border-zinc-700 hover:border-zinc-500"><Minus className="w-4 h-4" strokeWidth={2.5} /></button>
                            <div className="flex-1">
                              <input type="text" inputMode="decimal" value={localFenceRep} onFocus={(e) => e.target.select()} onChange={(e) => setLocalFenceRep(e.target.value)} onBlur={commitFenceRepChange} onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} className={`w-full text-center text-3xl font-black bg-[#050505] border border-zinc-700 rounded-sm py-2 focus:outline-none focus:border-emerald-500/50 transition ${parseFloat(localFenceRep) >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
                            </div>
                            <button onClick={() => handleFenceRepChange(0.01)} className="w-10 h-10 bg-zinc-800 hover:bg-zinc-700 text-white rounded-sm font-bold transition flex items-center justify-center border border-zinc-700 hover:border-zinc-500"><Plus className="w-4 h-4" strokeWidth={2.5} /></button>
                          </div>
                        </div>
                      </div>

                      <div className="border-t border-zinc-800 pt-2 mt-2">
                        <button onClick={handleAuth} className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-bold text-sm transition-all ${user ? 'bg-red-900/10 text-red-500 hover:bg-red-900/20 border border-red-900/30' : 'bg-emerald-700 text-white hover:bg-emerald-600'}`}>
                          {user ? <LogOut className="w-4 h-4" /> : <LogIn className="w-4 h-4" />}
                          {user ? t('sign_out') : t('sign_in_google')}
                        </button>
                      </div>

                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {showFeedback && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[60] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setShowFeedback(false)}>
          <div className="bg-[#09090b] border border-zinc-700 rounded-sm w-full max-w-md shadow-2xl p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowFeedback(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2"><div className="text-[#5865F2]"><MessageSquarePlus className="w-6 h-6" /></div> {t('feedback_title')}</h2>
            <p className="text-zinc-400 text-sm mb-4">{t('feedback_desc')}</p>
            <textarea
              className="w-full bg-[#050505] border border-zinc-700 rounded-sm p-3 text-white text-sm focus:outline-none focus:border-[#5865F2] h-32 resize-none placeholder-zinc-600"
              placeholder={t('feedback_placeholder')}
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
            ></textarea>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowFeedback(false)} className="px-4 py-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm transition text-sm font-bold">{t('cancel')}</button>

              {/* SPAM KORUMALI GÖNDER BUTONU */}
              <button
                onClick={sendFeedbackToDiscord}
                disabled={isSendingFeedback || !feedbackMessage.trim()}
                className={`px-4 py-2 text-white rounded-sm font-bold flex items-center gap-2 text-sm transition-all ${isSendingFeedback || !feedbackMessage.trim()
                  ? 'bg-zinc-600 cursor-not-allowed opacity-70'
                  : 'bg-[#5865F2] hover:bg-[#4752C4] shadow-lg shadow-indigo-500/20'
                  }`}
              >
                <Send className={`w-3 h-3 ${isSendingFeedback ? 'animate-pulse' : ''}`} />
                {isSendingFeedback ? '...' : t('send')}
              </button>

            </div>
          </div>
        </div>
      )}

      {showSettings && <Settings profile={profile} onUpdate={onUpdate} onClose={() => setShowSettings(false)} onReset={onReset} />}
    </>
  );
}

export default Navbar;
